import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { GoldenCircles, GridDots, VShape, CornerMark } from "./GeometricDecor";
import { hero } from "@/lib/content";
import { getLanguageFromPath } from "@/lib/language";

export default function HeroSection() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  const copy = hero[getLanguageFromPath(location.pathname)];

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    setTimeout(() => {
      el.style.transition = "opacity 0.9s ease, transform 0.9s ease";
      el.style.opacity = "1";
      el.style.transform = "translateY(0)";
    }, 200);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: "#004643" }}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20geometric%20shapes%20flowing%20lines%20minimal%20elegant%20studio%20workspace%20creative%20design%20agency%20background%20deep%20charcoal%20black%20sophisticated%20texture&width=1920&height=1080&seq=hero1&orientation=landscape"
          alt="Hero background"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,70,67,0.88)" }} />
      </div>

      {/* === GEOMETRIC DECORATIONS === */}

      {/* Large golden ratio circles – top right */}
      <GoldenCircles
        size={520}
        opacity={0.07}
        color="#acd1c6"
        className="top-[-80px] right-[-80px]"
        animated
      />

      {/* Medium circles – bottom left */}
      <GoldenCircles
        size={280}
        opacity={0.05}
        color="#acd1c6"
        className="bottom-[-60px] left-[-60px]"
      />

      {/* Dot grid – right side */}
      <GridDots
        cols={10}
        rows={8}
        gap={32}
        opacity={0.1}
        className="right-10 top-1/4"
      />

      {/* Dot grid – left side small */}
      <GridDots
        cols={5}
        rows={4}
        gap={28}
        opacity={0.07}
        className="left-8 bottom-32"
      />

      {/* V-shape – logó V betűje, nagy, háttérben */}
      <VShape
        width={340}
        height={220}
        opacity={0.06}
        color="#acd1c6"
        strokeWidth={1}
        className="top-1/3 right-1/4"
      />

      {/* V-shape – kisebb, bal oldal */}
      <VShape
        width={160}
        height={100}
        opacity={0.05}
        color="#acd1c6"
        strokeWidth={1}
        className="bottom-24 left-1/4"
      />

      {/* Corner marks – mérnöki sarokjelölők */}
      <CornerMark position="tl" size={32} opacity={0.25} className="top-24 left-8" />
      <CornerMark position="tr" size={32} opacity={0.25} className="top-24 right-8" />
      <CornerMark position="bl" size={32} opacity={0.2} className="bottom-16 left-8" />
      <CornerMark position="br" size={32} opacity={0.2} className="bottom-16 right-8" />

      {/* Horizontal rule lines – mérnöki vízszintes vonalak */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "38%",
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(to right, transparent, rgba(172,209,198,0.08) 20%, rgba(172,209,198,0.08) 80%, transparent)",
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          top: "62%",
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(to right, transparent, rgba(172,209,198,0.05) 20%, rgba(172,209,198,0.05) 80%, transparent)",
        }}
      />

      {/* Vertical accent line – bal oldal */}
      <div
        className="absolute pointer-events-none hidden lg:block"
        style={{
          left: "8%",
          top: "20%",
          bottom: "20%",
          width: "1px",
          background: "linear-gradient(to bottom, transparent, rgba(172,209,198,0.12) 30%, rgba(172,209,198,0.12) 70%, transparent)",
        }}
      />

      {/* === CONTENT === */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16 flex flex-col lg:flex-row items-center gap-12 lg:gap-0">
        {/* Left: Text */}
        <div className="flex-1 text-center lg:text-left">
          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-medium tracking-widest uppercase mb-8 backdrop-blur-sm"
            style={{ borderColor: "rgba(172,209,198,0.3)", backgroundColor: "rgba(172,209,198,0.08)", color: "#acd1c6" }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "#acd1c6" }} />
            {copy.badge}
          </div>

          <h1
            ref={titleRef}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'League Spartan', sans-serif", letterSpacing: "-0.02em" }}
          >
            {copy.titleTop}
            <br />
            <span style={{ color: "#acd1c6" }}>{copy.titleAccent}</span>
          </h1>

          <p className="text-white/60 text-base md:text-lg max-w-lg mx-auto lg:mx-0 mb-10 leading-relaxed" style={{ fontFamily: "'Garet', sans-serif" }}>
            {copy.text}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
            <button
              onClick={() => handleScroll("#portfolio")}
              className="group flex items-center gap-3 px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#acd1c6", color: "#004643" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#9bc4b8"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6"; }}
            >
              <span>{copy.primary}</span>
              <span
                className="w-6 h-6 flex items-center justify-center rounded-full group-hover:translate-x-1 transition-transform duration-300"
                style={{ backgroundColor: "#004643", color: "#acd1c6" }}
              >
                <i className="ri-arrow-right-line text-xs" />
              </span>
            </button>
            <button
              onClick={() => handleScroll("#contact")}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full border text-white text-sm font-medium hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ borderColor: "rgba(172,209,198,0.35)" }}
            >
              {copy.secondary}
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center lg:justify-start gap-8 mt-14 pt-8" style={{ borderTop: "1px solid rgba(172,209,198,0.15)" }}>
            {copy.stats.map((stat) => (
              <div key={stat.label} className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'League Spartan', sans-serif", color: "#acd1c6" }}>
                  {stat.value}
                </div>
                <div className="text-white/45 text-xs mt-0.5 tracking-widest uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Geometric composition + photo */}
        <div className="flex-1 flex items-center justify-center lg:justify-end relative w-full max-w-sm lg:max-w-none">
          <div className="relative w-80 h-80 md:w-96 md:h-96">

            {/* Middle ring – aránymetszet */}
            <div
              className="absolute rounded-full"
              style={{
                border: "1px solid rgba(172,209,198,0.2)",
                top: "12%", left: "12%", right: "12%", bottom: "12%",
              }}
            />
            {/* Inner ring */}
            <div
              className="absolute rounded-full"
              style={{
                border: "1px solid rgba(172,209,198,0.12)",
                top: "24%", left: "24%", right: "24%", bottom: "24%",
              }}
            />

            {/* Cross axes */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-full h-px" style={{ backgroundColor: "rgba(172,209,198,0.08)" }} />
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="h-full w-px" style={{ backgroundColor: "rgba(172,209,198,0.08)" }} />
            </div>

            {/* Photo in center circle */}
            <div
              className="absolute rounded-full overflow-hidden"
              style={{
                top: "15%", left: "15%", right: "15%", bottom: "15%",
                border: "2px solid rgba(172,209,198,0.3)",
              }}
            >
              <img
                src="https://storage.readdy-site.link/project_files/861a4be8-11ae-4d25-93ac-9354b438fa30/5eb6d1d6-0756-41ee-9d9b-f5c19cc7afda_Lakatos-Ivett.png?v=bb66885b13645dc6be3a9ce6e58b7bfd"
                alt="Lakatos Ivett"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Tick marks on outer ring – mérnöki beosztás */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
              <div
                key={deg}
                className="absolute"
                style={{
                  width: "1px",
                  height: deg % 90 === 0 ? "10px" : "6px",
                  backgroundColor: "#acd1c6",
                  opacity: deg % 90 === 0 ? 0.35 : 0.18,
                  top: "50%",
                  left: "50%",
                  transformOrigin: "top center",
                  transform: `rotate(${deg}deg) translateX(-50%) translateY(-${160}px)`,
                }}
              />
            ))}


          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" style={{ color: "#acd1c6" }}>
        <span className="text-xs tracking-widest uppercase opacity-50">{copy.scroll}</span>
        <div className="w-px h-8 animate-pulse" style={{ backgroundColor: "#acd1c6", opacity: 0.4 }} />
      </div>
    </section>
  );
}
